import type { ReactNode } from "react";
import { cx } from "@/shared/lib/cx";
import { AlertTriangle, Inbox, LoaderCircle } from "@/shared/ui/icons";
import type { IconComponent } from "./types";

export function EmptyState({
  title = "Nothing here yet",
  description,
  action,
  compact = false,
  icon: Icon = Inbox,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
  icon?: IconComponent;
  className?: string;
}) {
  return (
    <div className={cx("dw-empty", compact && "min-h-40 py-7", className)}>
      <span className="grid size-10 place-items-center rounded-xl bg-base-200 text-muted">
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <strong className="text-sm font-semibold text-base-content">{title}</strong>
      {description && <p className="m-0 max-w-md text-[12px] leading-5 text-muted">{description}</p>}
      {action}
    </div>
  );
}

// Stato vuoto a pagina intera: immagine, titolo, sottotitolo e CTA centrati nell'area contenuto.
export function Void({
  image,
  imageAlt = "",
  title,
  description,
  action,
  className,
}: {
  image?: string;
  imageAlt?: string;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx("dw-void", className)}>
      {image && <img className="dw-void-image" src={image} alt={imageAlt} />}
      <h1 className="dw-void-title">{title}</h1>
      {description && <p className="dw-void-description">{description}</p>}
      {action && <div className="dw-void-action">{action}</div>}
    </section>
  );
}

export function LoadingState({
  label = "Loading",
  compact = false,
}: {
  label?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={cx("flex items-center justify-center gap-2 text-muted", compact ? "min-h-40" : "min-h-72")}
      role="status"
      aria-live="polite"
    >
      <LoaderCircle className="size-5 animate-spin text-primary" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({
  title = "Unable to load this content",
  message,
  action,
  compact = false,
}: {
  title?: ReactNode;
  message?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={cx(
        "flex flex-col items-center justify-center gap-2 px-6 text-center",
        compact ? "min-h-40" : "min-h-72",
      )}
      role="alert"
    >
      <span className="grid size-10 place-items-center rounded-xl bg-error/10 text-error">
        <AlertTriangle className="size-5" />
      </span>
      <strong className="text-sm font-semibold text-base-content">{title}</strong>
      {message && <p className="m-0 max-w-md text-[12px] text-muted">{message}</p>}
      {action}
    </div>
  );
}
